// userReducer.js

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  fieldErrors: {},
  xpData: {
    totalXP: 0,
    level: 1,
    progressToNext: 0,
    xpNeeded: 100,
    progressPercentage: 0,
    dailyLoginStreak: 0,
    dailyPromptsAnswered: 0,
    meetIRLPreferencesSet: false,
    photoUploadCount: 0,
    aiChatCount: 0,
    recentActivity: []
  },
  xpLoading: false,
  xpError: null,
  xpHistory: {
    activities: [],
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      pages: 0
    }
  }
};

const userReducer = (state = initialState, action) => {
  switch (action.type) {
    // Registration cases
    case 'USER_REGISTER_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
        fieldErrors: {},
      };
    case 'USER_REGISTER_SUCCESS':
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        error: null,
        fieldErrors: {},
      };
    case 'USER_REGISTER_FAIL':
      return {
        ...state,
        loading: false,
        error: action.payload.message || action.payload,
        fieldErrors: action.payload.fieldErrors || {},
        isAuthenticated: false,
      };

    // Login cases
    case 'USER_LOGIN_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'USER_LOGIN_SUCCESS':
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        error: null,
      };
    case 'USER_LOGIN_FAIL':
      return {
        ...state,
        loading: false,
        error: action.payload,
        isAuthenticated: false,
      };

    // OTP verification cases
    case 'USER_VERIFY_OTP_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'USER_VERIFY_OTP_SUCCESS':
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        // Merge so fields missing from the verify response
        // (e.g. hasCompletedOnboarding) are preserved
        user: {
          ...state.user,
          ...action.payload.user,
        },
        token: action.payload.token,
        error: null,
      };
    case 'USER_VERIFY_OTP_FAIL':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Logout case
    case 'USER_LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      };

    // Get current user cases
    case 'USER_GET_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'USER_GET_SUCCESS':
      return {
        ...state,
        loading: false,
        user: action.payload,
        isAuthenticated: true,
        error: null,
      };
    case 'USER_GET_FAIL':
      return {
        ...state,
        loading: false,
        error: action.payload,
        isAuthenticated: false,
      };

    // Update user profile cases
    case 'USER_UPDATE_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'USER_UPDATE_SUCCESS':
      // Validate that we have valid user data before persisting
      const updatedUser = action.payload?.user || action.payload;

      if (!updatedUser || typeof updatedUser !== 'object') {
        console.error('Invalid user data in USER_UPDATE_SUCCESS:', action.payload);
        return {
          ...state,
          loading: false,
          error: 'Invalid user data received',
        };
      }

      // Merge updated user data with existing user data to preserve all fields
      const mergedUser = {
        ...state.user,
        ...updatedUser
      };

      // Persist updated user to localStorage with validation
      if (mergedUser) {
        try {
          localStorage.setItem('user', JSON.stringify(mergedUser));
        } catch (error) {
          console.error('Failed to persist user to localStorage:', error);
        }
      }
      return {
        ...state,
        loading: false,
        user: mergedUser,
        error: null,
      };
    case 'USER_UPDATE_FAIL':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Clear error
    case 'CLEAR_USER_ERROR':
      return {
        ...state,
        error: null,
        fieldErrors: {},
      };

    // XP Actions
    case 'AWARD_XP_REQUEST':
      return {
        ...state,
        xpLoading: true,
        xpError: null,
      };
    case 'AWARD_XP_SUCCESS':
      return {
        ...state,
        xpLoading: false,
        xpData: {
          ...state.xpData,
          totalXP: action.payload.totalXP,
          level: action.payload.level,
          // Update other fields as needed
        },
        xpError: null,
      };
    case 'AWARD_XP_FAIL':
      return {
        ...state,
        xpLoading: false,
        xpError: action.payload,
      };

    case 'FETCH_XP_PROFILE_REQUEST':
      return {
        ...state,
        xpLoading: true,
        xpError: null,
      };
    case 'FETCH_XP_PROFILE_SUCCESS':
      // Validate payload structure before assignment
      const profilePayload = action.payload;
      if (profilePayload && typeof profilePayload === 'object' && 
          typeof profilePayload.totalXP === 'number' && 
          typeof profilePayload.level === 'number') {
        return {
          ...state,
          xpLoading: false,
          xpData: profilePayload,
          xpError: null,
        };
      } else {
        console.warn('Invalid XP profile payload structure:', profilePayload);
        return {
          ...state,
          xpLoading: false,
          xpError: 'Invalid XP profile data received',
        };
      }
    case 'FETCH_XP_PROFILE_FAIL':
      return {
        ...state,
        xpLoading: false,
        xpError: action.payload,
      };

    case 'FETCH_XP_HISTORY_REQUEST':
      return {
        ...state,
        xpLoading: true,
        xpError: null,
      };
    case 'FETCH_XP_HISTORY_SUCCESS':
      // Validate payload structure before assignment
      const historyPayload = action.payload;
      if (historyPayload && typeof historyPayload === 'object' && 
          Array.isArray(historyPayload.activities) && 
          historyPayload.pagination && 
          typeof historyPayload.pagination === 'object') {
        return {
          ...state,
          xpLoading: false,
          xpHistory: historyPayload,
          xpError: null,
        };
      } else {
        console.warn('Invalid XP history payload structure:', historyPayload);
        return {
          ...state,
          xpLoading: false,
          xpError: 'Invalid XP history data received',
        };
      }
    case 'FETCH_XP_HISTORY_FAIL':
      return {
        ...state,
        xpLoading: false,
        xpError: action.payload,
      };

    case 'UPDATE_USER_XP':
      // Validate payload structure to prevent unexpected properties
      const updatePayload = action.payload;
      if (updatePayload && typeof updatePayload === 'object') {
        // Only allow specific XP-related fields to be updated
        const allowedFields = ['totalXP', 'level', 'progressToNext', 'xpNeeded', 'progressPercentage', 
                              'dailyLoginStreak', 'dailyPromptsAnswered', 'meetIRLPreferencesSet', 
                              'photoUploadCount', 'aiChatCount', 'recentActivity'];
        const filteredPayload = {};
        
        Object.keys(updatePayload).forEach(key => {
          if (allowedFields.includes(key)) {
            filteredPayload[key] = updatePayload[key];
          } else {
            console.warn(`Unexpected field in XP update: ${key}`);
          }
        });
        
        return {
          ...state,
          xpData: {
            ...state.xpData,
            ...filteredPayload,
          },
        };
      } else {
        console.warn('Invalid XP update payload:', updatePayload);
        return state;
      }

    default:
      return state;
  }
};

export default userReducer;
